import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { months } from '../../constants/dates';
import { SdoPositionsPublishObj } from '../../models/SdoInterface';
import { useSdoAdminReportsProvider } from '../../providers/SdoAdminReportsProvider';
import { useSdoApiError } from '../../providers/SdoApiErrorProvider';
import Loader from '../Loader/Loader';
import SpecialDayOffApiFailure from '../SpecialDayOff/SpecialDayOffApiFailure';
import './SdoAdminReportDetails.scss';
import SdoAdminReportDetailsHeader from './SdoAdminReportDetailsHeader';
import SdoAdminReportDetailsReview from './SdoAdminReportDetailsReview';
import SdoAdminReportDetailsStatus from './SdoAdminReportDetailsStatus';
import { sdoCycleStatus } from './SdoAdminReportHelper';
import { useLocation } from 'react-router-dom';

export default function SdoAdminReportDetails() {
  const { t } = useTranslation();
  const sdoAdminReportsContext = useSdoAdminReportsProvider();
  const [position, setPosition] = useState<any>('');
  const [sdoMonthYear, setSdoMonthYear] = useState<any>({
    sdoMonth: '',
    sdoYear: '',
    sdoMonthYearString: ''
  });
  const [status, setStatus] = useState('');
  const [positionsPublishData, setPositionsPublishData] =
    useState<SdoPositionsPublishObj>();
  const sdoApiErrorContext = useSdoApiError();
  const location = useLocation();
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    sdoAdminReportsContext.getSdoCurrentSubPeriodData();
    sdoApiErrorContext.setSdoErrorCode(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sdoAdminReportsContext?.sdoCurrentSubPeriodData?.month) return;

    setSdoMonthYear({
      sdoMonth: sdoAdminReportsContext?.sdoCurrentSubPeriodData?.month,
      sdoYear: sdoAdminReportsContext?.sdoCurrentSubPeriodData?.year,
      sdoMonthYearString:
        months[
          Number(sdoAdminReportsContext?.sdoCurrentSubPeriodData?.month) - 1
        ] +
        ' ' +
        sdoAdminReportsContext?.sdoCurrentSubPeriodData?.year
    });
  }, [sdoAdminReportsContext.sdoCurrentSubPeriodData]);

  useEffect(() => {
    if (sdoMonthYear.sdoMonth && sdoMonthYear.sdoYear) {
      sdoAdminReportsContext.getPositionsPublishData(sdoMonthYear);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdoMonthYear.sdoMonth, sdoMonthYear.sdoYear]);

  useEffect(() => {
    if (sdoMonthYear.sdoMonth && sdoMonthYear.sdoYear && refresh) {
      sdoAdminReportsContext.getPositionsPublishData(sdoMonthYear);
      setPosition({ ...position });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  useEffect(() => {
    if (!sdoAdminReportsContext.sdoPositionsPublishData?.cycleStatus) return;

    setPositionsPublishData(sdoAdminReportsContext.sdoPositionsPublishData);
    setStatus(sdoAdminReportsContext.sdoPositionsPublishData?.cycleStatus);
  }, [sdoAdminReportsContext.sdoPositionsPublishData]);

  useEffect(() => {
    if (!positionsPublishData) return;
    if (location.state) getPositionFromLimitsTab();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionsPublishData]);

  const getPositionFromLimitsTab = () => {
    const positionProps = location.state.position.replace(/\s/g, '').split('|');
    const positionObj = {
      base: positionProps[0],
      equipment: positionProps[1],
      rank: positionProps[2]
    };
    if (positionsPublishData) {
      const positionIndex = positionsPublishData.positions.findIndex(
        (position) => {
          return (
            position.pos.base === positionObj.base &&
            position.pos.equipment === positionObj.equipment &&
            position.pos.position === positionObj.rank
          );
        }
      );
      if (positionIndex !== -1)
        setPosition(positionsPublishData.positions[positionIndex]);
    }
  };

  const getTotalPositions = () => {
    if (!positionsPublishData) return 0;

    return (
      positionsPublishData?.totalPending + positionsPublishData?.totalPublished
    );
  };

  return sdoAdminReportsContext.sdoCurrentSubPeriodLoading ||
    sdoAdminReportsContext.positionsPublishLoading ? (
    <Loader loaderText="Loading Data" />
  ) : (
    <>
      <SdoAdminReportDetailsHeader
        positionsPublishData={positionsPublishData}
      />
      {/* banner: for open status */}
      {status && status === sdoCycleStatus.OPEN ? (
        <div className="sdo-report-details__submission-open-container">
          <div className="sdo-report-details__submission-open">
            <i className="icon-circle-flight" />
            <p>
              {' '}
              {t(
                'specialDayOffAdmin.specialDayOffAdminReportDetails.submissionOpen',
                { monthYear: sdoMonthYear?.sdoMonthYearString }
              )}
            </p>
          </div>
        </div>
      ) : (
        ''
      )}
      {/* banner: for closed status and not published */}
      {status &&
      status === sdoCycleStatus.OVERDUE &&
      positionsPublishData &&
      positionsPublishData?.totalPending > 0 ? (
        <div className="sdo-report-details____submission-unpublished-container">
          <div className="sdo-report-details____submission-unpublished">
            <i className="icon-Error-warning" />
            <div className="sdo-admin-report-stats__notification-text">
              <p className="sdo-admin-report-stats__notification-text1">
                {t(
                  'specialDayOffAdmin.sdoAdminReportStats.reportUnpublishedNotification1',
                  {
                    monthYear: sdoMonthYear?.sdoMonthYearString,
                    pendingPositions: positionsPublishData?.totalPending ?? 0,
                    totalPositions: getTotalPositions()
                  }
                )}
              </p>
              <p>
                {t(
                  'specialDayOffAdmin.sdoAdminReportStats.reportUnpublishedNotification2'
                )}
              </p>
            </div>
          </div>
        </div>
      ) : (
        ''
      )}
      {/* banner: when publish api fails */}
      {sdoApiErrorContext.sdoError ? (
        <div className="sdo-report-details____submission-unpublished-container">
          <SpecialDayOffApiFailure />
        </div>
      ) : (
        ''
      )}
      <SdoAdminReportDetailsStatus
        positionsPublishData={positionsPublishData}
        setPosition={setPosition}
      />
      <SdoAdminReportDetailsReview
        positionsPublishData={positionsPublishData}
        setPositionsPublishData={setPositionsPublishData}
        position={position}
        fromSetLimits={true}
        setPosition={setPosition}
        refresh={refresh}
        setRefresh={setRefresh}
      />
    </>
  );
}
